import React, { useMemo, useState } from 'react';
import Slider from 'react-slick';
import '../../../styles/slick/slick.css';
import '../../../styles/slick/slick-theme.css';
import css from './Sections.module.css';
import { ListingCard } from '../../../components';
import { isArrayLength } from '../../../util/genericHelpers';
import Icons from '../../../components/Icons/Icons';
import { categoryValue } from '../../../util/categories';

function SectionExplore(props) {
  const {
    viewport,
    intl,
    listings = [],
    isMobileLayout,
    useHistoryPush,
    sliderSettings = {
      dots: false,
      arrows: true,
      infinite: false,
      autoplay: false,
      autoplaySpeed: 2000,
      speed: 500,
      slidesToShow: 4,
      slidesToScroll: 1,
      variableWidth: false,
      focusOnSelect: false,
      centerMode: false,
      responsive: [
        { breakpoint: 1128, settings: { slidesToShow: 3 } },
        { breakpoint: 991, settings: { slidesToShow: 3 } },
        { breakpoint: 767, settings: { slidesToShow: 2, dots: true, arrows: false } },
        { breakpoint: 580, settings: { slidesToShow: 1, dots: true, arrows: false } },
      ],
    },
  } = props;

  // ✅ Category pills (exact order you requested)
  const productCategories = [
    { key: 'all', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.all' }) },
    { key: 'technicalSuits', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.technicalSuits' }) },
    { key: 'swimFins', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.swimFins' }) },
    { key: 'snorkels', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.snorkels' }) },
    { key: 'trainingSuits', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.trainingSuits' }) },
    { key: 'paddles', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.paddles' }) },
    { key: 'kickboards', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.kickboards' }) },
    { key: 'goggles', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.goggles' }) },
    { key: 'swimCaps', label: intl.formatMessage({ id: 'SectionExplore.categoryPills.swimCaps' }) },
  ];

  // ✅ Active filter (default: All)
  const [activeCategory, setActiveCategory] = useState('all');

  // ✅ Only featured listings
  const featuredListings = useMemo(
    () => listings.filter(l => l?.attributes?.publicData?.isFeatured === true),
    [listings]
  );

  // ✅ Featured listings filtered by category
  const filteredListings = useMemo(() => {
    if (activeCategory === 'all') return featuredListings;

    return featuredListings.filter(l => {
      // Support both keys in case your data uses either:
      const cat1Raw =
        l?.attributes?.publicData?.categoryLevel1 ||
        l?.attributes?.publicData?.pub_categoryLevel1;

      // Normalize: allow camelCase keys from older data to match canonical snake_case filter value
      const cat1 = categoryValue(cat1Raw);
      return cat1 === categoryValue(activeCategory);
    });
  }, [featuredListings, activeCategory]);

  const noItemsMessage = intl.formatMessage({ id: 'SectionExplore.noItemsMessage' });

  return (
    <div className={css.sectionExplore}>
      <div className={css.fixedWidthContainer}>
        <div className={css.topCollectionsWrapper}>
          <div className={css.sectionHead}>
            <div className={css.leftSec}>
              <h2>{intl.formatMessage({ id: 'SectionExplore.title' })}</h2>
            </div>
          </div>

          {/* ✅ Filter pills */}
          <div className={css.filterPills}>
            {productCategories.map((category, i) => {
              const isActive = activeCategory === category.key;

              return (
                <span
                  key={category.key}
                  role="button"
                  tabIndex={0}
                  className={isActive ? `${css.pill} ${css.activePill}` : css.pill}
                  onClick={() => setActiveCategory(category.key)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') setActiveCategory(category.key);
                  }}
                  aria-pressed={isActive}
                >
                  {i === 0 && <Icons name="gridIcon" />} {category.label}
                </span>
              );
            })}
          </div>

          {/* ✅ Featured + Category-filtered */}
          {isArrayLength(filteredListings) ? (
            <div className={css.topCollectionsContent}>
              <Slider {...{ ...sliderSettings, arrows: !isMobileLayout }}>
                {filteredListings.map((l, index) => (
                  <ListingCard
                    className={css.listingCard}
                    key={l?.id?.uuid || index}
                    listing={l}
                  />
                ))}
              </Slider>
            </div>
          ) : (
            <div className={css.noItemsMessage}>{noItemsMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SectionExplore;
